import React, { useRef, useState } from "react";
import { ActionIcon, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { notifyError } from "../../../utils/notify";
import {
  IconCheck,
  IconUpload,
  IconPhoto,
  IconCloudUpload,
  IconX,
} from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";
import { DiagramGender, DiagramSelection } from "./types/chart";
import {
  DIAGRAM_BODY_PART_FILTERS,
  DIAGRAM_PRESETS,
} from "./utils/diagramPresets";
import primaryAction from "../../common/primaryFilledButton.module.css";
import styles from "./DiagramLibrary.module.css";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

interface DiagramLibraryProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (selection: DiagramSelection) => void;
  defaultTab?: "presets" | "upload";
}

export default function DiagramLibrary({
  opened,
  onClose,
  onSelect,
  defaultTab = "presets",
}: DiagramLibraryProps) {
  const { uploadImage } = useApp();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [gender, setGender] = useState<DiagramGender>("female");
  const [bodyPartFilter, setBodyPartFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"presets" | "upload">(defaultTab);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const filtered = DIAGRAM_PRESETS.filter((d) => {
    if (d.gender !== gender) return false;
    if (bodyPartFilter === "all") return true;
    return d.bodyPart === bodyPartFilter || d.tags.includes(bodyPartFilter);
  });

  const handleAttachPreset = () => {
    if (!selectedId) return;
    const diagram = DIAGRAM_PRESETS.find((d) => d.id === selectedId);
    if (diagram) {
      onSelect({ source: "preset", diagram });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    setActiveTab(defaultTab);
    onClose();
  };

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
        return {
          isValid: false,
          error: "File type not supported. Please use JPEG, PNG, or WebP.",
        };
      }
    }
    if (file.size > MAX_SIZE) {
      return {
        isValid: false,
        error: "File too large. Please use files smaller than 5MB.",
      };
    }
    return { isValid: true };
  };

  const processFile = async (file: File) => {
    if (isUploading) return;

    if (!businessInfo?.id) {
      notifyError("Salon information not available. Please refresh the page.", {
        title: "Upload Failed",
      });
      return;
    }

    const validation = validateFile(file);
    if (!validation.isValid) {
      notifyError(validation.error, { title: "Upload Failed" });
      return;
    }

    try {
      setIsUploading(true);
      const response = await uploadImage({
        image: file,
        business_location_id: businessInfo.id,
        optimization_type: "web",
      });

      const imageUrl = response.cloudfront_url || response.s3_url;
      onSelect({
        source: "upload",
        imageUrl,
        imageName: file.name.replace(/\.[^/.]+$/, ""),
      });
      handleClose();
    } catch (error) {
      console.error("Upload error:", error);
      notifyError(
        error instanceof Error
          ? error.message
          : "Failed to upload image. Please try again.",
        { title: "Upload Failed" },
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items?.length) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const next = prev - 1;
      if (next === 0) setIsDragOver(false);
      return next;
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const selectedPresetName = selectedId
    ? DIAGRAM_PRESETS.find((d) => d.id === selectedId)?.name
    : null;

  const switchTab = (tab: "presets" | "upload") => {
    setActiveTab(tab);
    setSelectedId(null);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      withCloseButton={false}
      padding={0}
      radius="lg"
      size="100%"
      overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
      styles={{
        inner: { padding: "var(--mantine-spacing-md)" },
        content: {
          maxWidth: 1120,
          width: "100%",
          maxHeight: "85vh",
        },
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
          height: "min(85vh, 900px)",
        },
      }}
    >
      <div className={styles.shell}>
        <div className={styles.shellHeader}>
          <h2 className={styles.shellTitle}>Diagram Library</h2>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="xl"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close diagram library"
          >
            <IconX size={20} />
          </ActionIcon>
        </div>

        <div
          className={styles.tabNav}
          role="tablist"
          aria-label="Library source"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "presets"}
            className={`${styles.tabButton} ${activeTab === "presets" ? styles.tabButtonActive : ""}`}
            onClick={() => switchTab("presets")}
          >
            Preset diagrams
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "upload"}
            className={`${styles.tabButton} ${activeTab === "upload" ? styles.tabButtonActive : ""}`}
            onClick={() => switchTab("upload")}
          >
            Custom uploads
          </button>
        </div>

        {activeTab === "presets" ? (
          <div className={styles.presetToolbar}>
            <div className={styles.genderSegment}>
              <button
                type="button"
                className={`${styles.genderButton} ${gender === "female" ? styles.genderButtonActive : ""}`}
                onClick={() => {
                  setGender("female");
                  setBodyPartFilter("all");
                  setSelectedId(null);
                }}
              >
                Female
              </button>
              <button
                type="button"
                className={`${styles.genderButton} ${gender === "male" ? styles.genderButtonActive : ""}`}
                onClick={() => {
                  setGender("male");
                  setBodyPartFilter("all");
                  setSelectedId(null);
                }}
              >
                Male
              </button>
            </div>
            <div className={styles.categoryRow}>
              {DIAGRAM_BODY_PART_FILTERS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className={`${styles.categoryChip} ${bodyPartFilter === chip.id ? styles.categoryChipActive : ""}`}
                  onClick={() => {
                    setBodyPartFilter(chip.id);
                    setSelectedId(null);
                  }}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className={styles.scrollBody}>
          {activeTab === "presets" ? (
            filtered.length === 0 ? (
              <div className={styles.emptyPresets}>
                <Text size="sm">No diagrams found for this category.</Text>
              </div>
            ) : (
              <div className={styles.presetGrid}>
                {filtered.map((diagram) => {
                  const isSelected = selectedId === diagram.id;
                  return (
                    <div
                      key={diagram.id}
                      className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
                      onClick={() => setSelectedId(diagram.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedId(diagram.id);
                        }
                      }}
                    >
                      <div className={styles.cardPreview}>
                        <img
                          src={diagram.imageUrl}
                          alt=""
                          className={styles.cardPreviewImage}
                        />
                      </div>
                      <div
                        className={`${styles.cardLabel} ${isSelected ? styles.cardLabelSelected : ""}`}
                      >
                        <Text
                          size="sm"
                          fw={500}
                          c={isSelected ? "teal" : "dark"}
                        >
                          {diagram.name}
                        </Text>
                      </div>
                      {isSelected ? (
                        <div className={styles.checkBadge}>
                          <IconCheck size={14} />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <Stack gap="lg">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
              />
              <div
                className={styles.dropZone}
                data-drag-over={isDragOver || undefined}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <IconCloudUpload
                  size={40}
                  style={{
                    margin: "0 auto var(--mantine-spacing-md)",
                    display: "block",
                    color: isDragOver
                      ? "var(--mantine-color-teal-6)"
                      : "var(--mantine-color-gray-5)",
                  }}
                />
                <Text fw={500} size="sm" c="dark.8" mb={4}>
                  Drop images here or click to upload
                </Text>
                <Text size="xs" c="dimmed">
                  PNG, JPG, WebP up to 5MB
                </Text>
                <Group justify="center" mt="md">
                  <Button
                    variant="filled"
                    color="teal"
                    className={primaryAction.primaryFilled}
                    size="sm"
                    leftSection={<IconUpload size={16} />}
                    loading={isUploading}
                    disabled={isUploading}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    {isUploading ? "Uploading…" : "Choose file"}
                  </Button>
                </Group>
              </div>
              <div className={styles.uploadHint}>
                <IconPhoto
                  size={36}
                  style={{
                    margin: "0 auto var(--mantine-spacing-xs)",
                    display: "block",
                    color: "var(--mantine-color-gray-3)",
                  }}
                />
                <Text size="sm" c="dimmed">
                  Images upload to your library and open in the chart editor.
                </Text>
              </div>
            </Stack>
          )}
        </div>

        <div className={styles.shellFooter}>
          <div className={styles.footerHint}>
            {activeTab === "presets" ? (
              selectedPresetName ? (
                <>
                  Selected:{" "}
                  <Text span fw={500} c="dark.8">
                    {selectedPresetName}
                  </Text>
                </>
              ) : (
                "Click a diagram to select it"
              )
            ) : (
              "Upload an image to use as the chart base"
            )}
          </div>
          <div className={styles.footerActions}>
            <Button variant="subtle" color="gray" onClick={handleClose}>
              Cancel
            </Button>
            {activeTab === "presets" ? (
              <Button
                variant="filled"
                color="teal"
                className={primaryAction.primaryFilled}
                disabled={!selectedId}
                onClick={handleAttachPreset}
              >
                Attach selected
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
